import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Copy, ExternalLink, Plus, Presentation } from "lucide-react";
import { api, type Demo } from "../api";
import { Alert, PageHeader } from "../components/Page";

/** Today + n days as an <input type="date"> value. */
function inDays(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

/** End of the chosen day in the viewer's time zone, as ISO. */
export function endOfDay(dateValue: string): string | null {
  if (!dateValue) return null;
  const d = new Date(`${dateValue}T23:59:59`);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export function demoState(d: Demo): { label: string; pill: string } {
  if (!d.enabled) return { label: d.owns_workspace ? "Off" : "Page off", pill: "pill-deleted" };
  if (d.expires_at && new Date(d.expires_at) <= new Date()) return { label: "Expired", pill: "pill-failed" };
  return { label: "Live", pill: "pill-succeeded" };
}

export function CopyLink({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className="btn-ghost"
      onClick={async () => {
        try { await navigator.clipboard.writeText(url); setCopied(true); window.setTimeout(() => setCopied(false), 2000); }
        catch { /* clipboard blocked — the link is visible to copy by hand */ }
      }}
    >
      {copied ? <><Check className="h-4 w-4" aria-hidden="true" /> Copied</> : <><Copy className="h-4 w-4" aria-hidden="true" /> Copy link</>}
    </button>
  );
}

export function Demos() {
  const [demos, setDemos] = useState<Demo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [client, setClient] = useState("");
  const [website, setWebsite] = useState("");
  const [quota, setQuota] = useState("200");
  const [expires, setExpires] = useState(inDays(30));
  const [creating, setCreating] = useState(false);
  const [formErr, setFormErr] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try { setDemos(await api.listDemos()); setError(null); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  async function create(e: React.FormEvent) {
    e.preventDefault();
    const q = Number(quota);
    if (client.trim().length < 2) { setFormErr("Enter the client's name."); return; }
    if (!Number.isInteger(q) || q < 1) { setFormErr("The page limit must be a whole number of 1 or more."); return; }
    setCreating(true); setFormErr(null);
    try {
      await api.createDemo({
        client_name: client.trim(),
        client_website: website.trim() || null,
        page_quota: q,
        expires_at: endOfDay(expires),
      });
      setClient(""); setWebsite("");
      await refresh();
    } catch (err) {
      setFormErr(err instanceof Error ? err.message : String(err));
    } finally { setCreating(false); }
  }

  return (
    <div>
      <PageHeader
        title="Client demos"
        description={<>
          A private page for one prospect, showing their own PDFs converted. Each demo has its own workspace and page limit,
          so it never mixes with a live customer. Adding a PDF converts nothing — you pick the pages and accept the cost per document.
        </>}
      />

      {error && <Alert onDismiss={() => setError(null)}>{error}</Alert>}

      <form onSubmit={create} className="card mb-6" noValidate>
        <div className="card-header">
          <h2 className="text-base font-semibold text-ink flex items-center gap-2">
            <Plus className="h-4 w-4 text-brand-500" aria-hidden="true" /> New demo
          </h2>
        </div>
        <div className="card-body grid grid-cols-1 md:grid-cols-12 gap-3 items-start">
          <div className="md:col-span-4">
            <label htmlFor="nd-client" className="block text-sm font-medium text-slate-700 mb-1">
              Client name <span className="text-rose-600" aria-hidden="true">*</span>
            </label>
            <input id="nd-client" className="input" value={client} onChange={(e) => setClient(e.target.value)}
              placeholder="Wheaton Park District" required aria-required="true" />
          </div>
          <div className="md:col-span-3">
            <label htmlFor="nd-site" className="block text-sm font-medium text-slate-700 mb-1">Client website</label>
            <input id="nd-site" className="input" type="url" value={website} onChange={(e) => setWebsite(e.target.value)}
              placeholder="https://wheatonparkdistrict.com" aria-describedby="nd-site-help" />
            <p id="nd-site-help" className="mt-1 text-xs text-slate-500">Crawled only if you choose to, from the demo.</p>
          </div>
          <div className="md:col-span-2">
            <label htmlFor="nd-quota" className="block text-sm font-medium text-slate-700 mb-1">Page limit</label>
            <input id="nd-quota" className="input" type="number" min={1} value={quota} onChange={(e) => setQuota(e.target.value)}
              aria-describedby="nd-quota-help" />
            <p id="nd-quota-help" className="mt-1 text-xs text-slate-500">Caps what this demo can spend.</p>
          </div>
          <div className="md:col-span-2">
            <label htmlFor="nd-exp" className="block text-sm font-medium text-slate-700 mb-1">Available until</label>
            <input id="nd-exp" className="input" type="date" value={expires} onChange={(e) => setExpires(e.target.value)} />
          </div>
          <div className="md:col-span-1 flex md:pt-6">
            <button type="submit" className="btn-primary w-full" disabled={creating}>
              {creating ? <><span className="spinner" aria-hidden="true" /> Creating…</> : "Create"}
            </button>
          </div>
          {formErr && <p role="alert" className="md:col-span-12 text-sm text-rose-700">{formErr}</p>}
        </div>
      </form>

      <div className="card">
        <div className="card-header">
          <h2 className="text-base font-semibold text-ink flex items-center gap-2">
            <Presentation className="h-4 w-4 text-brand-500" aria-hidden="true" /> Demos ({demos.length})
          </h2>
        </div>
        {loading ? (
          <div className="card-body text-sm text-slate-500 flex items-center gap-2"><span className="spinner" aria-hidden="true" /> Loading…</div>
        ) : demos.length === 0 ? (
          <div className="card-body text-sm text-slate-500">No demos yet. Create one above.</div>
        ) : (
          <ul className="divide-y divide-slate-200">
            {demos.map((d) => {
              const st = demoState(d);
              const shown = d.items.filter((i) => i.published).length;
              return (
                <li key={d.id} className="px-5 py-4 flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Link to={`/demos/${d.id}`} className="font-medium text-ink hover:underline">{d.client_name}</Link>
                      <span className={st.pill}>{st.label}</span>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {shown} of {d.items.length} document{d.items.length === 1 ? "" : "s"} on the page
                      {" · "}{d.tenant.pages_used.toLocaleString()} of {d.tenant.page_quota?.toLocaleString() ?? "∞"} pages used
                      {" · "}${d.usd_spent.toFixed(2)} spent
                      {" · "}{d.view_count} view{d.view_count === 1 ? "" : "s"}
                      {d.expires_at && <> · until {new Date(d.expires_at).toLocaleDateString()}</>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <CopyLink url={d.url} />
                    <a href={d.url} target="_blank" rel="noopener noreferrer" className="btn-ghost">
                      <ExternalLink className="h-4 w-4" aria-hidden="true" /> Open<span className="sr-only"> {d.client_name} demo page (new tab)</span>
                    </a>
                    <Link to={`/demos/${d.id}`} className="btn-secondary">Manage<span className="sr-only"> {d.client_name}</span></Link>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
